import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import {
  LayoutGrid,
  Building2,
  FileStack,
  Landmark,
  BellRing,
  LogOut,
  Menu,
  X,
  HardHat,
  Users,
  MapPin,
  Hammer,
  Home as HomeIcon,
  ClipboardList,
  Receipt,
  LifeBuoy,
  Megaphone,
  Wallet,
  FileText,
  UserCog,
  History,
  Inbox,
} from "lucide-react";
import SavvyLogo from "./SavvyLogo";
import CompanyBrandingBar from "./CompanyBrandingBar";
import ImpersonationBanner from "./ImpersonationBanner";

const NAV_MAP = {
  platform_admin: [
    { to: "/platform", label: "Overview", icon: LayoutGrid, end: true },
    { to: "/platform/companies", label: "Companies", icon: Building2 },
    { to: "/platform/plans", label: "Subscription Plans", icon: FileStack },
    { to: "/platform/subscriptions", label: "Subscriptions", icon: Landmark },
    { to: "/platform/demo-requests", label: "Demo Requests", icon: Inbox },
    { to: "/platform/notifications", label: "Notifications", icon: BellRing },
    { to: "/platform/audit", label: "Audit Log", icon: History },
  ],
  company_admin: [
    { to: "/company", label: "Overview", icon: LayoutGrid, end: true },
    { to: "/company/sites", label: "Project Sites", icon: MapPin },
    { to: "/company/projects", label: "Projects", icon: HardHat },
    { to: "/company/towers", label: "Towers", icon: Building2 },
    { to: "/company/units", label: "Units", icon: HomeIcon },
    { to: "/company/customers", label: "Customers", icon: Users },
    { to: "/company/ownerships", label: "Unit Ownerships", icon: ClipboardList },
    { to: "/company/updates", label: "Construction Updates", icon: Hammer },
    { to: "/company/alerts", label: "Alerts", icon: Megaphone },
    { to: "/company/schedules", label: "Payment Schedules", icon: Wallet },
    { to: "/company/payments", label: "Payment References", icon: Receipt },
    { to: "/company/invoices", label: "Invoices", icon: FileText },
    { to: "/company/tickets", label: "Support Tickets", icon: LifeBuoy },
    { to: "/company/users", label: "Company Users", icon: UserCog },
    { to: "/company/audit", label: "Audit Log", icon: History },
    { to: "/company/profile", label: "Profile", icon: Building2 },
  ],
  company_user: [
    { to: "/company", label: "Overview", icon: LayoutGrid, end: true },
    { to: "/company/projects", label: "Projects", icon: HardHat },
    { to: "/company/updates", label: "Construction Updates", icon: Hammer },
    { to: "/company/alerts", label: "Alerts", icon: Megaphone },
    { to: "/company/tickets", label: "Support Tickets", icon: LifeBuoy },
    { to: "/company/schedules", label: "Payment Schedules", icon: Wallet },
    { to: "/company/payments", label: "Payment References", icon: Receipt },
    { to: "/company/invoices", label: "Invoices", icon: FileText },
  ],
  customer: [
    { to: "/portal", label: "Overview", icon: LayoutGrid, end: true },
    { to: "/portal/units", label: "My Units", icon: HomeIcon },
    { to: "/portal/updates", label: "Updates", icon: Hammer },
    { to: "/portal/schedules", label: "Payment Schedule", icon: Wallet },
    { to: "/portal/payments", label: "Paid Details", icon: Receipt },
    { to: "/portal/invoices", label: "Invoices", icon: FileText },
    { to: "/portal/alerts", label: "Alerts", icon: Megaphone },
    { to: "/portal/tickets", label: "Support", icon: LifeBuoy },
  ],
};

const ROLE_LABEL = {
  platform_admin: "Platform Admin",
  company_admin: "Company Admin",
  company_user: "Company User",
  customer: "Customer",
};

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const items = NAV_MAP[user?.role] || [];

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-stone-100 flex">
      {/* Mobile menu button */}
      <button
        data-testid="mobile-menu-toggle"
        onClick={() => setOpen(!open)}
        className="lg:hidden fixed top-3 left-3 z-50 bg-stone-950 text-white p-2 border border-stone-800"
      >
        {open ? <X size={18} /> : <Menu size={18} />}
      </button>

      {/* Sidebar */}
      <aside
        className={`${
          open ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 fixed lg:sticky top-0 left-0 z-40 h-screen w-64 bg-stone-950 text-stone-300 border-r border-stone-800 transition-transform flex flex-col`}
        data-testid="dashboard-sidebar"
      >
        <div className="px-6 py-6 border-b border-stone-800">
          <SavvyLogo variant="onSidebar" size={42} showTagline tagline="Ops Console" testId="sidebar-logo" />
        </div>

        <nav className="flex-1 overflow-y-auto py-4">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setOpen(false)}
              data-testid={`nav-${item.to.split("/").pop() || "home"}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-2.5 text-sm font-medium transition-colors border-l-2 ${
                  isActive
                    ? "bg-stone-900 text-white border-l-brand-500"
                    : "border-l-transparent hover:bg-stone-900 hover:text-white"
                }`
              }
            >
              <item.icon size={16} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-stone-800 p-4">
          <div className="text-[10px] tracking-[0.2em] text-stone-500 uppercase mb-1">
            Signed in as
          </div>
          <div className="text-white text-sm font-semibold truncate">
            {user?.name || user?.email}
          </div>
          <div className="text-stone-500 text-xs mt-0.5">
            {ROLE_LABEL[user?.role] || user?.role}
          </div>
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className="mt-3 w-full flex items-center justify-center gap-2 bg-transparent border border-stone-700 text-stone-300 hover:bg-brand-500 hover:border-brand-500 hover:text-white text-xs font-bold uppercase tracking-wider py-2 transition-colors"
          >
            <LogOut size={14} /> Logout
          </button>
          <div className="mt-5 text-center" data-testid="sidebar-powered-by">
            <div className="text-[10px] tracking-[0.28em] text-stone-600 uppercase mb-1.5">
              Powered by
            </div>
            <div className="text-[13px] tracking-[0.04em] text-stone-300 font-bold leading-tight">
              SavvySpark Solutions<br />Private Limited
            </div>
            <div className="text-[10px] tracking-[0.18em] text-stone-600 mt-1.5 font-mono">
              www.savvyspark.in
            </div>
          </div>
        </div>
      </aside>

      {open && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Main */}
      <main className="flex-1 min-w-0 lg:ml-0">
        <ImpersonationBanner />
        <div className="max-w-7xl mx-auto p-6 md:p-8 pt-16 lg:pt-8">
          {(user?.role === "company_admin" || user?.role === "company_user") && (
            <CompanyBrandingBar />
          )}
          {children}
        </div>
      </main>
    </div>
  );
}
